import Semaphore from "./Semaphore.js";

var once = fn => {
    var already_called = false;
    return () => {
        if (!already_called) {
            already_called = true;
            fn();
        }
    };
};

/**
 * Simple mutex class.
 * @example
 * const mutex = new Mutex()
 * const release = await mutex.acquire()
 * release() // to release mutex
 */
const Mutex = function() {
    var semaphore = new Semaphore(1);

    /**
     * Determine if the lock is available.
     * @returns {Boolean} - true if lock is available, false otherwise
     */
    this.available = () => semaphore.available();

    /**
     * Get a lock if available.
     *
     * @returns {null|Function} - if the lock is available, returns a
     * function to release it. Otherwise returns null. The release function can
     * be called multiple times, it will only release once.
     * @example
     * const mutex = new Mutex()
     * const release = mutex.acquire_try()
     * if (release) release()
     */
    this.acquire_try = () => {
        var release = once(() => semaphore.release());
        return (semaphore.acquire_try()) ? release : null;
    };

    /**
     * Acquire a lock.
     * @returns {Promise} - returns a Promise that resolves to a release function. The release
     * function can be called multiple times, it will only release once.
     */
    this.acquire = () => {
        var release = once(() => semaphore.release());
        return semaphore.acquire().then(() => release);
    };
};

export default Mutex;
